import type { GettextInterface } from "../../../../../../shared/I18n";
import type { ReportContent } from "../../../../viewjobOneGraphModel";

import { SubmitStatus } from "./SubmitStatus";
import { useReportJobFormState } from "../../../../../../shared/components/ReportJobForm";
import eventEmitter from "../../../../../../shared/util/eventEmitter";
import { httpPostJson } from "../../../../../native-module/httpPostJson";

import { useCallback, useEffect, useState } from "react";

const SUBMIT_REPORT_EVENT = "submitReport";

export function useModalState(): [boolean, () => void, () => void] {
  const [isOpen, setShowModal] = useState(false);
  const open = useCallback(() => setShowModal(true), [setShowModal]);
  const close = useCallback(() => setShowModal(false), [setShowModal]);
  return [isOpen, open, close];
}

interface ReportContentResponse {
  readonly message: string;
}

export function useSubmitReport(reportContent?: ReportContent): SubmitStatus {
  const [status, updateSubmitStatus] = useState(SubmitStatus.INACTIVE);

  useEffect(() => {
    const submitReport = ({ reason, moreInfo }: { reason: string; moreInfo: string }) => {
      if (!reportContent) {
        updateSubmitStatus(SubmitStatus.ERROR);
        return;
      }

      const { apiUrl, buildPayload } = reportContent;
      const payload = buildPayload(reason, moreInfo);

      httpPostJson<ReportContentResponse>(apiUrl, payload)
        .then((response) => {
          response.message === "OK"
            ? updateSubmitStatus(SubmitStatus.SUCCESS)
            : updateSubmitStatus(SubmitStatus.ERROR);
        })
        .catch(() => {
          updateSubmitStatus(SubmitStatus.ERROR);
        });
    };

    eventEmitter.on(SUBMIT_REPORT_EVENT, submitReport);

    return () => {
      // on unmount, remove the event listener
      eventEmitter.off(SUBMIT_REPORT_EVENT, submitReport);
    };
  }, [reportContent]);

  return status;
}

export function useReportContentForm(
  handleClose: () => void,
  showPhotoLabel: boolean,
  gt: GettextInterface,
  showNotHiringReason = false,
  moreInfoMaxLength = 300,
) {
  const {
    reportOptions,
    selectedOption,
    reason,
    moreInfo,
    setMoreInfo,
    selectReason,
    canSubmit,
    reset,
  } = useReportJobFormState(gt, showPhotoLabel, moreInfoMaxLength, showNotHiringReason);

  const close = useCallback(() => {
    reset();
    handleClose();
  }, [reset, handleClose]);

  const submit = useCallback(() => {
    close();
    return eventEmitter.emit(SUBMIT_REPORT_EVENT, { reason, moreInfo });
  }, [close, moreInfo, reason]);

  const selectProblemOption = useCallback(
    (item: { value: string }) => {
      selectReason(item.value);
    },
    [selectReason],
  );

  return {
    contentProblemOptions: reportOptions,
    selectProblemOption,
    selectedOption,
    moreInfo,
    setMoreInfo,
    close,
    submit,
    canSubmit,
  };
}
